import React, { useState, useEffect, useMemo } from 'react';
import { 
  DollarSign, Users, TrendingUp, TrendingDown, Building, 
  PieChart, BarChart3, Calendar, Download, Eye, FileSpreadsheet,
  ChevronLeft, ChevronRight, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RePieChart, Pie, Cell, BarChart, Bar, Legend
} from 'recharts';
import { db } from '../../../lib/db';
import { formatUSDCurrency } from '../../../lib/usPayrollCalculations';
import { getMonthName, getCurrentMonth, getCurrentYear } from '../../../lib/dateUtils';
import { exportToCSV } from '../../../lib/export';
import { toast } from 'sonner';

const CHART_COLORS = ['#14b8a6', '#0ea5e9', '#8b5cf6', '#f59e0b', '#ef4444', '#22c55e', '#ec4899'];

export default function PayrollOverview() {
  const [loading, setLoading] = useState(true);
  const [payslips, setPayslips] = useState([]);
  const [users, setUsers] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [payrollRuns, setPayrollRuns] = useState([]);
  const [selectedYear, setSelectedYear] = useState(getCurrentYear());
  const [compareYear, setCompareYear] = useState(getCurrentYear() - 1);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [payslipsData, usersData, bonusData, runsData] = await Promise.all([
        db.payslips.toArray(),
        db.users.toArray(),
        db.bonuses.toArray(),
        db.payrollRuns.toArray()
      ]);
      setPayslips(payslipsData);
      setUsers(usersData.filter(u => u.role !== 'admin'));
      setBonuses(bonusData);
      setPayrollRuns(runsData.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const yearPayslips = useMemo(() => {
    return payslips.filter(p => p.year === selectedYear);
  }, [payslips, selectedYear]);

  const prevYearPayslips = useMemo(() => {
    return payslips.filter(p => p.year === compareYear);
  }, [payslips, compareYear]);

  const monthlyData = useMemo(() => {
    const months = Array.from({ length: 12 }, (_, i) => i + 1);
    return months.map(month => {
      const currentMonthPayslips = yearPayslips.filter(p => p.month === month);
      const prevMonthPayslips = prevYearPayslips.filter(p => p.month === month);
      
      return {
        month: getMonthName(month).substring(0, 3),
        fullMonth: getMonthName(month),
        current: currentMonthPayslips.reduce((sum, p) => sum + (Number(p.netPay) || 0), 0),
        previous: prevMonthPayslips.reduce((sum, p) => sum + (Number(p.netPay) || 0), 0),
        employees: currentMonthPayslips.length
      };
    });
  }, [yearPayslips, prevYearPayslips]);

  const departmentData = useMemo(() => {
    const deptMap = {};
    yearPayslips.forEach(p => {
      const employee = users.find(u => u.id === p.userId);
      const dept = employee?.profile?.department || 'Unknown';
      if (!deptMap[dept]) {
        deptMap[dept] = { 
          name: dept, 
          total: 0, 
          headcount: new Set(), 
          avgSalary: 0 
        };
      }
      deptMap[dept].total += (Number(p.netPay) || 0);
      deptMap[dept].headcount.add(p.userId);
    });
    return Object.values(deptMap).map(d => ({
      ...d,
      headcount: d.headcount.size,
      avgSalary: d.headcount.size > 0 ? d.total / d.headcount.size : 0
    }));
  }, [yearPayslips, users]);

  const totals = useMemo(() => {
    const currentGross = yearPayslips.reduce((sum, p) => sum + (Number(p.grossPay) || Number(p.grossEarnings) || 0), 0);
    const currentNet = yearPayslips.reduce((sum, p) => sum + (Number(p.netPay) || 0), 0);
    const currentDeductions = yearPayslips.reduce((sum, p) => sum + (Number(p.totalDeductions) || ((Number(p.totalPreTax) || 0) + (Number(p.totalTaxes) || 0) + (Number(p.totalPostTax) || 0)) || 0), 0);
    
    const prevGross = prevYearPayslips.reduce((sum, p) => sum + (Number(p.grossPay) || Number(p.grossEarnings) || 0), 0);
    const prevNet = prevYearPayslips.reduce((sum, p) => sum + (Number(p.netPay) || 0), 0);
    
    const totalEmployees = new Set(yearPayslips.map(p => p.userId)).size;
    const totalBonuses = bonuses
      .filter(b => b.status === 'approved' && new Date(b.appliedAt).getFullYear() === selectedYear)
      .reduce((sum, b) => sum + (Number(b.amount) || 0), 0);
    
    const yoyGrowth = prevNet > 0 ? ((currentNet - prevNet) / prevNet) * 100 : 0;
    
    return {
      currentGross,
      currentNet,
      currentDeductions,
      prevNet,
      totalEmployees,
      totalBonuses,
      yoyGrowth,
      avgSalary: totalEmployees > 0 ? currentNet / (totalEmployees * 12) : 0
    };
  }, [yearPayslips, prevYearPayslips, bonuses, selectedYear]);

  const costBreakdown = useMemo(() => {
    const totalGross = yearPayslips.reduce((sum, p) => sum + (Number(p.grossPay) || Number(p.grossEarnings) || 0), 0);
    const totalBonuses = bonuses
      .filter(b => b.status === 'approved' && new Date(b.appliedAt).getFullYear() === selectedYear)
      .reduce((sum, b) => sum + (Number(b.amount) || 0), 0);
    
    const breakdown = { regular: 0, overtime: 0, bonuses: totalBonuses, taxes: 0, deductions: 0 };
    yearPayslips.forEach(p => {
      // Handle US payslip format (earnings.regular) and old format (earnings.basic)
      breakdown.regular += Number(p.earnings?.regular) || Number(p.earnings?.basic) || 0;
      breakdown.overtime += Number(p.earnings?.overtime) || 0;
      breakdown.taxes += Number(p.totalTaxes) || 0;
      breakdown.deductions += Number(p.totalPreTax) || Number(p.totalPostTax) || 0;
    });
    
    return [
      { name: 'Regular Pay', value: breakdown.regular },
      { name: 'Overtime', value: breakdown.overtime },
      { name: 'Bonuses', value: breakdown.bonuses },
      { name: 'Taxes Withheld', value: breakdown.taxes },
      { name: 'Deductions', value: breakdown.deductions }
    ].filter(d => d.value > 0);
  }, [yearPayslips, bonuses, selectedYear]);

  const handleExport = () => {
    const report = monthlyData.map(d => ({
      Month: d.fullMonth,
      Year: selectedYear,
      Employees: d.employees,
      'Total Payroll': d.current,
      'YoY Change': d.previous > 0 ? ((d.current - d.previous) / d.previous * 100).toFixed(1) + '%' : 'N/A'
    }));
    exportToCSV(report, `CEO_Payroll_Overview_${selectedYear}`);
    toast.success('Report exported');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payroll Overview</h1>
          <p className="text-gray-500 mt-1">Organization-wide payroll insights and analytics</p>
        </div>
        <div className="flex gap-2">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => setSelectedYear(selectedYear - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="font-bold text-lg">{selectedYear}</span>
            <Button variant="ghost" size="icon" onClick={() => setSelectedYear(selectedYear + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <Button variant="outline" onClick={handleExport}>
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Export Report
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm bg-gradient-to-r from-teal-500 to-emerald-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-teal-100">Total Payroll (YTD)</p>
                <p className="text-3xl font-bold mt-1">{formatUSDCurrency(totals.currentNet)}</p>
                <div className="flex items-center gap-1 mt-2">
                  {totals.yoyGrowth >= 0 ? (
                    <ArrowUpRight className="w-4 h-4" />
                  ) : (
                    <ArrowDownRight className="w-4 h-4" />
                  )}
                  <span className="text-sm">
                    {Math.abs(totals.yoyGrowth).toFixed(1)}% vs last year
                  </span>
                </div>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Employees</p>
                <p className="text-2xl font-bold text-gray-900">{totals.totalEmployees}</p>
                <p className="text-sm text-gray-500 mt-1">Active on payroll</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Avg. Monthly Salary</p>
                <p className="text-2xl font-bold text-gray-900">{formatUSDCurrency(totals.avgSalary)}</p>
                <p className="text-sm text-gray-500 mt-1">Per employee</p>
              </div>
              <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-violet-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Bonuses</p>
                <p className="text-2xl font-bold text-amber-600">{formatUSDCurrency(totals.totalBonuses)}</p>
                <p className="text-sm text-gray-500 mt-1">Approved this year</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Payroll Runs */}
      {payrollRuns.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-teal-600" />
              Recent Payroll Runs
            </CardTitle>
            <CardDescription>Latest completed payroll processing</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Period</TableHead>
                  <TableHead>Pay Group</TableHead>
                  <TableHead className="text-center">Employees</TableHead>
                  <TableHead className="text-right">Gross Pay</TableHead>
                  <TableHead className="text-right">Net Pay</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-right">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payrollRuns.slice(0, 5).map((run) => (
                  <TableRow key={run.id}>
                    <TableCell className="font-medium">{run.periodName || 'N/A'}</TableCell>
                    <TableCell>{run.payGroupName || 'All'}</TableCell>
                    <TableCell className="text-center">{run.employeeCount || 0}</TableCell>
                    <TableCell className="text-right">{formatUSDCurrency(run.totals?.grossPay || 0)}</TableCell>
                    <TableCell className="text-right font-semibold">{formatUSDCurrency(run.totals?.netPay || 0)}</TableCell>
                    <TableCell className="text-center">
                      <Badge className={
                        run.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                        run.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }>
                        {run.status || 'Unknown'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-gray-500">
                      {run.createdAt ? new Date(run.createdAt).toLocaleDateString() : 'N/A'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="trends" className="space-y-6">
        <TabsList className="bg-gray-100/80">
          <TabsTrigger value="trends">Trends</TabsTrigger>
          <TabsTrigger value="department">By Department</TabsTrigger>
          <TabsTrigger value="costs">Cost Analysis</TabsTrigger>
        </TabsList>

        {/* Trends */}
        <TabsContent value="trends" className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Monthly Payroll Trend</CardTitle>
                  <CardDescription>Year-over-year comparison</CardDescription>
                </div>
                <Select value={compareYear.toString()} onValueChange={(v) => setCompareYear(parseInt(v))}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(5)].map((_, i) => {
                      const year = getCurrentYear() - i - 1;
                      return (
                        <SelectItem key={year} value={year.toString()}>
                          Compare to {year}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
                    <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(v) => `$${v / 1000}k`} />
                    <Tooltip 
                      formatter={(value) => formatUSDCurrency(value)}
                      labelFormatter={(label) => monthlyData.find(d => d.month === label)?.fullMonth}
                    />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="current" 
                      stroke="#14b8a6" 
                      fill="#14b8a6" 
                      fillOpacity={0.3}
                      name={`${selectedYear}`}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="previous" 
                      stroke="#9ca3af" 
                      fill="#9ca3af" 
                      fillOpacity={0.1}
                      name={`${compareYear}`}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Summary Table */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Monthly Summary</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Month</TableHead>
                    <TableHead className="text-center">Employees</TableHead>
                    <TableHead className="text-right">{selectedYear}</TableHead>
                    <TableHead className="text-right">{compareYear}</TableHead>
                    <TableHead className="text-right">Change</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {monthlyData.map((month, idx) => {
                    const change = month.previous > 0 
                      ? ((month.current - month.previous) / month.previous) * 100 
                      : 0;
                    return (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{month.fullMonth}</TableCell>
                        <TableCell className="text-center">{month.employees}</TableCell>
                        <TableCell className="text-right font-semibold">{formatUSDCurrency(month.current)}</TableCell>
                        <TableCell className="text-right text-gray-500">{formatUSDCurrency(month.previous)}</TableCell>
                        <TableCell className="text-right">
                          {month.previous > 0 ? (
                            <Badge className={change >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                              {change >= 0 ? '+' : ''}{change.toFixed(1)}%
                            </Badge>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Department View */}
        <TabsContent value="department" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle>Payroll by Department</CardTitle>
                <CardDescription>Distribution of payroll costs</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RePieChart>
                      <Pie
                        data={departmentData}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        dataKey="total"
                        nameKey="name"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {departmentData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatUSDCurrency(value)} />
                    </RePieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle>Department Details</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Department</TableHead>
                      <TableHead className="text-center">Headcount</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Avg/Person</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {departmentData.sort((a, b) => b.total - a.total).map((dept, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
                            />
                            <span className="font-medium">{dept.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">{dept.headcount}</TableCell>
                        <TableCell className="text-right font-semibold">{formatUSDCurrency(dept.total)}</TableCell>
                        <TableCell className="text-right text-gray-600">{formatUSDCurrency(dept.avgSalary)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          {/* Department Bar Chart */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Headcount vs Payroll by Department</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={departmentData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" stroke="#6b7280" fontSize={12} />
                    <YAxis yAxisId="left" stroke="#14b8a6" fontSize={12} tickFormatter={(v) => `$${v / 1000}k`} />
                    <YAxis yAxisId="right" orientation="right" stroke="#8b5cf6" fontSize={12} />
                    <Tooltip />
                    <Legend />
                    <Bar yAxisId="left" dataKey="total" fill="#14b8a6" name="Total Payroll" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="right" dataKey="headcount" fill="#8b5cf6" name="Headcount" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cost Analysis */}
        <TabsContent value="costs" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle>Cost Breakdown</CardTitle>
                <CardDescription>Total compensation structure</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RePieChart>
                      <Pie
                        data={costBreakdown}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        dataKey="value"
                        nameKey="name"
                      >
                        {costBreakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatUSDCurrency(value)} />
                      <Legend />
                    </RePieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle>Cost Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {costBreakdown.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
                      />
                      <span className="font-medium">{item.name}</span>
                    </div>
                    <span className="font-bold">{formatUSDCurrency(item.value)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between p-4 bg-teal-50 rounded-lg border border-teal-200">
                  <span className="font-semibold text-gray-900">Total Compensation</span>
                  <span className="text-xl font-bold text-teal-600">
                    {formatUSDCurrency(costBreakdown.reduce((sum, c) => sum + (Number(c.value) || 0), 0))}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Gross vs Net Comparison */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Gross vs Net Analysis</CardTitle>
              <CardDescription>Understanding payroll deduction impact</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-6 bg-blue-50 rounded-lg text-center">
                  <p className="text-sm text-blue-600 mb-2">Total Gross</p>
                  <p className="text-3xl font-bold text-blue-700">{formatUSDCurrency(totals.currentGross)}</p>
                </div>
                <div className="p-6 bg-red-50 rounded-lg text-center">
                  <p className="text-sm text-red-600 mb-2">Total Deductions</p>
                  <p className="text-3xl font-bold text-red-700">{formatUSDCurrency(totals.currentDeductions)}</p>
                  <p className="text-sm text-red-500 mt-2">
                    {totals.currentGross > 0 
                      ? `${((totals.currentDeductions / totals.currentGross) * 100).toFixed(1)}% of gross`
                      : '0%'
                    }
                  </p>
                </div>
                <div className="p-6 bg-emerald-50 rounded-lg text-center">
                  <p className="text-sm text-emerald-600 mb-2">Total Net</p>
                  <p className="text-3xl font-bold text-emerald-700">{formatUSDCurrency(totals.currentNet)}</p>
                  <p className="text-sm text-emerald-500 mt-2">
                    {totals.currentGross > 0 
                      ? `${((totals.currentNet / totals.currentGross) * 100).toFixed(1)}% of gross`
                      : '0%'
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
